import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import api from "../constants/api";
import { useAuth } from "../components/ui/AuthContext.jsx";
import { useConfirm } from "../components/ui/ConfirmationModal.jsx";
import { canAccessWartung, canEnterHoursUnlimited, canSeeLeitstand, ROLE_ADMIN } from "../constants/roles.js";
import { showError, showSuccess, handleError } from "../utils/toast.js";
import { smartBack } from "../constants/nav";
import CollapsibleBlock from "../components/ui/CollapsibleBlock";
import { ObjektProjekteList } from "../components/Objekte/ObjektLinkedLists.jsx";
import AppButton from "../components/ui/AppButton.jsx";
import DetailMapsRouteLink from "../components/ui/DetailMapsRouteLink.jsx";
import {
  detailPageHeaderNavClassName,
  detailPageHeaderNavActionsClassName,
  detailPageHeaderNavSecondaryShellClassName,
  entityMoreMenuAccentItemClassName,
  entityMoreMenuDangerItemClassName,
  entityMoreMenuItemClassName,
  entityMoreMenuItemEmojiClassName,
  entityMoreMenuPanelClassName,
  entityMoreMenuTriggerClassName,
  entityInfoBlockHeaderShellClassName,
  entityInfoBlockHeaderActionsClassName,
  entityInfoBlockEditButtonClassName,
  successButtonLoadingClassName,
} from "../design/tokens.js";
import { Z_INDEX } from "../components/ui/zIndex.js";
import { Field, Input, Textarea } from "../components/forms/shared/FormAtoms.jsx";
import WartungUndZugaenge from "../components/projekt/WartungUndZugaenge";
import KundenBlockOfferten from "../components/Kunden/KundenBlockOfferten.jsx";
import KundenBlockServiceauftraege from "../components/Kunden/KundenBlockServiceauftraege.jsx";
import ObjektBlockDateien from "../modules/objekte/components/ObjektBlockDateien.jsx";
import SharePointEntityLink from "../components/sharepoint/SharePointEntityLink.jsx";
import DmsTreePanel from "../components/dms/DmsTreePanel.jsx";
import EntityInlineFileLinks from "../components/ui/EntityInlineFileLinks.jsx";
import { buildObjektInlineFiles } from "../utils/entityInlineFilePresets.js";
import ObjektBlockKommentare from "../modules/objekte/components/ObjektBlockKommentare.jsx";
import ObjektBlockPendenzen from "../modules/objekte/components/ObjektBlockPendenzen.jsx";
import { Building2, User } from "lucide-react";
import { PROJEKT_TYP_LABEL } from "../constants/projektTypVisuals.js";
import {
  OBJEKT_PROJEKT_CREATE_TYPEN,
  projektCreatePath,
  serviceCreatePath,
} from "../utils/objektHubLinks.js";
import ObjektMessprotokolleList from "../components/Objekte/ObjektMessprotokolleList.jsx";
import EntityCountsStrip from "../components/ui/EntityCountsStrip.jsx";
import { buildObjektCountsStripItems } from "../utils/objektCountsStrip.js";
import { formatAktivArchiviertPair } from "@shared/serviceFalltypCounts.js";
import ModalOrSheet from "../components/ui/ModalOrSheet.jsx";
export default function ObjektDetails() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
    const {
    roleIdIn,
    user: authUser
  } = useAuth();
  const isManager = roleIdIn([1, 2, 3, 4]);
  const isAdminUser = Number(authUser?.rolle_id) === ROLE_ADMIN;
  const canWartung = canAccessWartung(authUser);
  const [objekt, setObjekt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [projekte, setProjekte] = useState([]);
  const [archivedProjekte, setArchivedProjekte] = useState([]);
  const [pendenzenCount, setPendenzenCount] = useState(0);
  const mountedRef = useRef(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState(null);
  const [transferKundeOpts, setTransferKundeOpts] = useState([]);
  const [transferKundeQ, setTransferKundeQ] = useState("");
  const [transferKundeLoading, setTransferKundeLoading] = useState(false);
  const [transferKundeOpen, setTransferKundeOpen] = useState(false);
  const [transferPreview, setTransferPreview] = useState(null);
  const [transferPreviewLoading, setTransferPreviewLoading] = useState(false);
  const [transferReason, setTransferReason] = useState("");
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const transferKundeDebounceRef = useRef(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const createMenuRef = useRef(null);
  const createBtnRef = useRef(null);
  const [createMenuPos, setCreateMenuPos] = useState({ top: 0, left: 0, width: 224 });
  const [svc, setSvc] = useState({
    sicherheit: {
      ready: false,
      show: false
    },
    ladestation: {
      ready: false,
      show: false
    },
    pv: {
      ready: false,
      show: false
    }
  });
  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await api.get(`/objekte/${id}`);
      if (!mountedRef.current) return;
      setObjekt(res.data);
      document.title = `${res.data?.name || "Objekt"} · WireTrack`;
    } catch (err) {
      if (!mountedRef.current) return;
      setObjekt(null);
      if (err?.response?.status === 404) {
        setLoadError("not_found");
        return;
      }
      setLoadError("error");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [id]);
  const loadRelated = useCallback(async () => {
    if (!objekt?.id) return;
    try {
      const projRes = await api.get(`/objekte/${objekt.id}/projekte-alle`);
      if (!mountedRef.current) return;
      const allProj = Array.isArray(projRes.data) ? projRes.data : [];
      setProjekte(allProj.filter((p) => !p.archiviert));
      setArchivedProjekte(allProj.filter((p) => p.archiviert));
    } catch {
      if (!mountedRef.current) return;
      setProjekte([]);
      setArchivedProjekte([]);
    }
  }, [objekt?.id]);
  const loadSvc = useCallback(async kundeId => {
    let darfAdminGruppe = false;
    try {
      const u = JSON.parse(sessionStorage.getItem("user") || "{}");
      darfAdminGruppe = canEnterHoursUnlimited(u);
    } catch {}
    if (!darfAdminGruppe) {
      if (mountedRef.current) {
        setSvc({
          sicherheit: {
            ready: true,
            show: false
          },
          ladestation: {
            ready: true,
            show: false
          },
          pv: {
            ready: true,
            show: false
          }
        });
      }
      return;
    }
    const mods = ["sicherheit", "ladestation", "pv"];
    const out = {};
    await Promise.all(mods.map(async m => {
      try {
        const [c1, c2] = await Promise.all([api.get(`/service/credentials/exists`, {
          params: {
            kunde_id: kundeId,
            modul: m
          }
        }), api.get(`/service/contracts/exists`, {
          params: {
            kunde_id: kundeId,
            modul: m
          }
        })]);
        out[m] = {
          ready: true,
          show: Number(c1?.data?.count || 0) > 0 || Number(c2?.data?.count || 0) > 0
        };
      } catch {
        out[m] = {
          ready: true,
          show: false
        };
      }
    }));
    if (mountedRef.current) setSvc(out);
  }, []);
  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  useEffect(() => {
    const onDoc = e => {
      if (moreOpen && moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
      if (
        createMenuOpen &&
        createMenuRef.current &&
        !createMenuRef.current.contains(e.target) &&
        createBtnRef.current &&
        !createBtnRef.current.contains(e.target)
      ) {
        setCreateMenuOpen(false);
      }
    };
    const onEsc = e => {
      if (e.key === "Escape") {
        setMoreOpen(false);
        setCreateMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [moreOpen, createMenuOpen]);
  const loadPendenzenCount = useCallback(async () => {
    try {
      const res = await api.get(`/pendenzen/objekt-exists/${id}`);
      if (mountedRef.current) setPendenzenCount(Number(res.data?.count || 0));
    } catch {
      if (mountedRef.current) setPendenzenCount(0);
    }
  }, [id]);
  useEffect(() => {
    if (objekt) {
      loadRelated();
      loadPendenzenCount();
      if (objekt.kunde_id) loadSvc(objekt.kunde_id);
    }
  }, [objekt, loadRelated, loadSvc, loadPendenzenCount]);
  const startEdit = () => {
    setEditForm({
      name: objekt.name || "",
      adresse: objekt.adresse || "",
      plz: objekt.plz || "",
      ort: objekt.ort || "",
      kontakt_vor_ort_name: objekt.kontakt_vor_ort_name || "",
      kontakt_vor_ort_tel: objekt.kontakt_vor_ort_tel || "",
      kontakt_vor_ort_email: objekt.kontakt_vor_ort_email || "",
      zugang_info: objekt.zugang_info || "",
      notizen: objekt.notizen || ""
    });
    setEditing(true);
  };
  const handleSave = async () => {
    if (!editForm.name?.trim()) return showError("Name erforderlich");
    setSaving(true);
    try {
      await api.put(`/objekte/${id}`, editForm);
      showSuccess("Gespeichert");
      setEditing(false);
      load();
    } catch (err) {
      showError(err, {
        fallbackKey: "Fehler"
      });
    } finally {
      setSaving(false);
    }
  };
  const handleArchive = async () => {
    const {
      ok
    } = await confirm({
      title: objekt.is_archived ? "Dearchivieren?" : "Archivieren?",
      message: objekt.is_archived ? `„${objekt.name}" wird wieder aktiviert.` : `„${objekt.name}" wird archiviert.`,
      confirmLabel: objekt.is_archived ? "Aktivieren" : "Archivieren"
    });
    if (!ok) return;
    try {
      await api.put(`/objekte/${id}`, {
        is_archived: !objekt.is_archived
      });
      showSuccess(objekt.is_archived ? "Aktiviert" : "Archiviert");
      load();
    } catch (err) {
      handleError(err);
    }
  };
  const handleDelete = async () => {
    const {
      ok
    } = await confirm({
      title: "Objekt löschen?",
      message: `„${objekt.name}" wird endgültig gelöscht.`,
      variant: "danger",
      confirmLabel: "Löschen"
    });
    if (!ok) return;
    try {
      await api.delete(`/objekte/${id}`);
      showSuccess("In Papierkorb verschoben");
      navigate(`/kunden/${objekt.kunde_id}`);
    } catch (err) {
      showError(err, {
        fallbackKey: "Fehler"
      });
    }
  };
  const loadTransferKunden = useCallback(async q => {
    setTransferKundeLoading(true);
    try {
      const {
        data
      } = await api.get("/kunden/options", {
        params: {
          q: q || undefined
        }
      });
      if (!mountedRef.current) return;
      setTransferKundeOpts(Array.isArray(data) ? data : []);
    } catch {
      if (mountedRef.current) setTransferKundeOpts([]);
    } finally {
      if (mountedRef.current) setTransferKundeLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!transferOpen) return;
    setTransferTargetId(null);
    setTransferPreview(null);
    setTransferReason("");
    setTransferKundeQ("");
    setTransferKundeOpen(false);
    loadTransferKunden("");
  }, [transferOpen, loadTransferKunden]);
  useEffect(() => {
    if (!transferOpen || !objekt?.id) return;
    const tid = transferTargetId;
    if (!tid || Number(tid) === Number(objekt.kunde_id)) {
      setTransferPreview(null);
      setTransferPreviewLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setTransferPreviewLoading(true);
      try {
        const {
          data
        } = await api.get(`/objekte/${objekt.id}/transfer-preview`, {
          params: {
            target_kunde_id: tid
          }
        });
        if (!cancelled) setTransferPreview(data?.ok ? data : null);
      } catch {
        if (!cancelled) setTransferPreview(null);
      } finally {
        if (!cancelled) setTransferPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transferOpen, transferTargetId, objekt?.id, objekt?.kunde_id]);
  const closeTransferModal = () => {
    setTransferOpen(false);
    setTransferTargetId(null);
    setTransferPreview(null);
    setTransferReason("");
    setTransferKundeOpen(false);
  };
  const handleTransferSubmit = async () => {
    if (!objekt?.id || !transferTargetId || transferReason.trim().length < 3) return;
    setTransferSubmitting(true);
    try {
      await api.post(`/objekte/${objekt.id}/transfer`, {
        kunde_id: transferTargetId,
        reason: transferReason.trim()
      });
      showSuccess("Objekt wurde der neuen Verwaltung zugeordnet");
      closeTransferModal();
      await load();
    } catch (err) {
      showError(err, {
        fallbackKey: "Transfer fehlgeschlagen"
      });
    } finally {
      if (mountedRef.current) setTransferSubmitting(false);
    }
  };
  const toggleCreateMenu = useCallback(() => {
    if (!createBtnRef.current) return;
    const r = createBtnRef.current.getBoundingClientRect();
    const menuWidth = 224;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const left = Math.max(8, Math.min(r.right - menuWidth, vw - menuWidth - 8));
    setCreateMenuPos({ top: r.bottom + 8, left, width: menuWidth });
    setCreateMenuOpen(v => !v);
  }, []);

  const goCreate = useCallback((target) => {
    if (!objekt?.kunde_id || objekt?.is_archived) return;
    if (target === "service") {
      navigate(serviceCreatePath(objekt.kunde_id, objekt.id));
    } else if (OBJEKT_PROJEKT_CREATE_TYPEN.includes(target)) {
      navigate(projektCreatePath(target, objekt.kunde_id, objekt.id));
    }
    setCreateMenuOpen(false);
  }, [navigate, objekt?.id, objekt?.is_archived, objekt?.kunde_id]);

  const grad = "bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const glass = "app-frame";
  const btnCls = "px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm text-blue-100 border border-white/15 transition min-h-[44px]";
  if (loading) {
    return <div className={`min-h-screen w-full ${grad}`}>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className={`rounded-2xl h-20 mb-6 animate-pulse border ${glass}`} />
          <div className="grid gap-4">
            <div className={`rounded-2xl h-40 animate-pulse border ${glass}`} />
            <div className={`rounded-2xl h-40 animate-pulse border ${glass}`} />
          </div>
        </div>
      </div>;
  }
  if (!objekt && loadError === "not_found") {
    return <div className={`min-h-screen w-full ${grad}`}>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className={`rounded-2xl p-5 border ${glass}`}>
            <h1 className="text-lg font-semibold text-blue-100/95">Objekt nicht gefunden</h1>
            <p className="mt-2 text-sm text-blue-200/80 leading-relaxed max-w-xl">
              Das Objekt existiert nicht oder wurde gelöscht.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => smartBack(navigate, "/kunden")}>
                Zurück
              </AppButton>
              <AppButton type="button" variant="primary" size="lg" onClick={load}>
                Erneut laden
              </AppButton>
            </div>
          </div>
        </div>
      </div>;
  }
  if (!objekt && loadError === "error") {
    return <div className={`min-h-screen w-full ${grad}`}>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className={`rounded-2xl p-5 border ${glass}`}>
            <h1 className="text-lg font-semibold text-blue-100/95">Objekt konnte nicht geladen werden</h1>
            <p className="mt-2 text-sm text-blue-200/80 leading-relaxed max-w-xl">
              Die Objektdaten sind momentan nicht verfügbar. Bitte prüfe die Verbindung und versuche es erneut.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => smartBack(navigate, "/kunden")}>
                Zurück
              </AppButton>
              <AppButton type="button" variant="primary" size="lg" onClick={load}>
                Erneut laden
              </AppButton>
            </div>
          </div>
        </div>
      </div>;
  }
  if (!objekt) return null;
  const kundenLabel = [objekt.kunden_firma, [objekt.kunden_vorname, objekt.kunden_nachname].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
  const adresseStr = [objekt.adresse, [objekt.plz, objekt.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const showS = svc.sicherheit.ready && svc.sicherheit.show;
  const showL = svc.ladestation.ready && svc.ladestation.show;
  const showP = svc.pv.ready && svc.pv.show;
  const showZugaenge = canWartung && (showS || showL || showP);
  const labelCls = "text-[11px] uppercase tracking-wider text-blue-200/50 mb-0.5 font-medium";
  const valCls = "text-sm text-blue-100/90";
  const inputCls = "w-full rounded-lg bg-[#0b1324] border border-white/10 px-3 py-2 text-sm text-blue-100 placeholder-blue-200/40 focus:outline-none focus:ring-2 focus:ring-blue-400/40";
  const subtleCls = "text-blue-200/70";
  const darfErstellen = isManager && objekt.kunde_id && !objekt.is_archived;
  return <div className={`min-h-screen w-full mobile-overflow-guard ${grad}`}>
      <div className="mx-auto max-w-6xl px-3 sm:px-4 py-4 sm:py-6 min-w-0">
        <div className="grid grid-cols-1 gap-2.5 sm:gap-3 min-w-0">

          {/* ──── Header (nur Navigation, wie KundenDetails) ──── */}
          <div className={`rounded-2xl px-3 sm:px-4 py-3 border flex flex-col sm:flex-row sm:items-center sm:justify-between min-w-0 ${glass}`}>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2 text-blue-100/90 min-w-0">
                <span aria-hidden className="shrink-0">🏢</span>
                <span className="truncate min-w-0">{objekt.name}</span>
                {objekt.is_archived ? (
                  <span className="text-xs px-2 py-0.5 rounded font-medium bg-amber-500/20 text-amber-400 shrink-0">
                    Archiviert
                  </span>
                ) : null}
              </h1>
              {objekt.id ? (
                <p className="text-xs text-blue-200/60 mt-0.5 tabular-nums">#{objekt.id}</p>
              ) : null}
            </div>
            <div className={`${detailPageHeaderNavActionsClassName} mt-3 sm:mt-0`}>
              <AppButton type="button" onClick={() => smartBack(navigate, [`/kunden/${objekt.kunde_id}`, "/kunden", "/"])} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Zurück</AppButton>
              <AppButton type="button" onClick={() => navigate(`/kunden/${objekt.kunde_id}`)} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Zum Kunden</AppButton>
              <AppButton type="button" onClick={() => navigate("/meine-aufgaben")} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Meine Aufgaben</AppButton>
              {canSeeLeitstand(authUser) ? (
                <AppButton type="button" onClick={() => navigate("/leitstand/alle")} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Übersicht</AppButton>
              ) : null}
              <AppButton type="button" onClick={() => navigate("/profil")} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Mein Profil</AppButton>
              <AppButton type="button" onClick={() => navigate("/")} variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Dashboard</AppButton>
            </div>
          </div>

          {/* ──── Standort-Details (wie Kundendaten: Erstellen + Bearbeiten) ──── */}
          <section className={`rounded-2xl px-2.5 py-2 sm:px-3 sm:py-3 border border-default bg-surface text-primary app-frame min-w-0 ${moreOpen || createMenuOpen ? "relative z-[1300]" : ""}`}>
            <div className={entityInfoBlockHeaderShellClassName}>
              <h2 className="text-lg sm:text-xl font-semibold flex items-start gap-2 min-w-0 text-blue-100/90 max-md:text-base max-md:leading-snug sm:min-w-[12rem] sm:flex-1">
                <Building2 className="w-5 h-5 shrink-0 mt-0.5 max-md:w-4 max-md:h-4" />
                <span className="min-w-0 flex flex-wrap items-center gap-2">
                  <span className="line-clamp-2">Standort-Details</span>
                  {objekt.is_archived && (
                    <span className="text-xs px-2 py-0.5 rounded font-medium bg-amber-500/20 text-amber-400">
                      Archiviert
                    </span>
                  )}
                </span>
              </h2>
              {!editing && (
                <div className={entityInfoBlockHeaderActionsClassName}>
                  {darfErstellen && (
                    <div className="relative shrink-0">
                      <AppButton
                        ref={createBtnRef}
                        type="button"
                        variant="primary"
                        size="sm"
                        className={entityInfoBlockEditButtonClassName}
                        onClick={toggleCreateMenu}
                        aria-haspopup="menu"
                        aria-expanded={createMenuOpen ? "true" : "false"}
                        title="Neu für dieses Objekt"
                      >
                        Erstellen
                      </AppButton>
                      {createMenuOpen &&
                        createPortal(
                          <div
                            ref={createMenuRef}
                            role="menu"
                            aria-label="Erstellen-Menü"
                            style={{
                              position: "fixed",
                              top: createMenuPos.top,
                              left: createMenuPos.left,
                              width: createMenuPos.width,
                              zIndex: Z_INDEX.selectPortal,
                              pointerEvents: "auto",
                            }}
                            className="rounded-xl border border-default shadow-xl p-1 bg-surface-elevated"
                          >
                            {OBJEKT_PROJEKT_CREATE_TYPEN.map((typ) => (
                              <AppButton
                                key={typ}
                                type="button"
                                variant="ghost"
                                role="menuitem"
                                onClick={() => goCreate(typ)}
                                className={entityMoreMenuItemClassName}
                              >
                                {PROJEKT_TYP_LABEL[typ] ? `Projekt · ${PROJEKT_TYP_LABEL[typ]}` : typ}
                              </AppButton>
                            ))}
                            <AppButton
                              type="button"
                              variant="ghost"
                              role="menuitem"
                              onClick={() => goCreate("service")}
                              className={entityMoreMenuItemClassName}
                            >
                              Serviceauftrag
                            </AppButton>
                          </div>,
                          document.body
                        )}
                    </div>
                  )}
                  {isManager && (
                    <AppButton
                      type="button"
                      onClick={startEdit}
                      variant="secondary"
                      size="sm"
                      className={`${entityInfoBlockEditButtonClassName} ${detailPageHeaderNavSecondaryShellClassName}`}
                      title="Bearbeiten"
                      aria-label="Bearbeiten"
                    >
                      Bearbeiten
                    </AppButton>
                  )}
                  {(isManager || isAdminUser) && (
                    <div className="relative shrink-0" ref={moreRef}>
                      <AppButton
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setMoreOpen(v => !v)}
                        aria-haspopup="menu"
                        aria-expanded={moreOpen ? "true" : "false"}
                        aria-label="Mehr Aktionen"
                        title="Mehr Aktionen"
                        className={entityMoreMenuTriggerClassName}
                      >
                        <span className="text-lg leading-none font-semibold px-1 tracking-wide select-none" aria-hidden>
                          ...
                        </span>
                      </AppButton>
                      {moreOpen && (
                        <div
                          role="menu"
                          aria-label="Mehr Aktionen"
                          className={entityMoreMenuPanelClassName}
                          style={{ zIndex: Z_INDEX.selectPortal }}
                        >
                          {isManager && (
                            <AppButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              role="menuitem"
                              onClick={() => {
                                setMoreOpen(false);
                                handleArchive();
                              }}
                              className={entityMoreMenuItemClassName}
                            >
                              {objekt.is_archived ? "Aktivieren" : "Archivieren"}
                              <span className={entityMoreMenuItemEmojiClassName} aria-hidden>
                                {objekt.is_archived ? "↩" : "📦"}
                              </span>
                            </AppButton>
                          )}
                          {isAdminUser && (
                            <AppButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              role="menuitem"
                              onClick={() => {
                                setMoreOpen(false);
                                setTransferOpen(true);
                              }}
                              className={entityMoreMenuAccentItemClassName}
                              title="Objekt einer anderen Verwaltung (Kunden-Stamm) zuordnen"
                            >
                              Verwaltung wechseln
                              <span className={entityMoreMenuItemEmojiClassName} aria-hidden>
                                ↔
                              </span>
                            </AppButton>
                          )}
                          {isManager && (
                            <AppButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              role="menuitem"
                              onClick={() => {
                                setMoreOpen(false);
                                handleDelete();
                              }}
                              className={entityMoreMenuDangerItemClassName}
                            >
                              Löschen
                              <span className={entityMoreMenuItemEmojiClassName} aria-hidden>
                                🗑
                              </span>
                            </AppButton>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {editing ? <div className="space-y-3">
                <div><div className={labelCls}>Name *</div><Input value={editForm.name} onChange={e => setEditForm(p => ({
                ...p,
                name: e.target.value
              }))} className={inputCls} /></div>
                <div><div className={labelCls}>Adresse</div><Input value={editForm.adresse} onChange={e => setEditForm(p => ({
                ...p,
                adresse: e.target.value
              }))} className={inputCls} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div><div className={labelCls}>PLZ</div><Input value={editForm.plz} onChange={e => setEditForm(p => ({
                  ...p,
                  plz: e.target.value
                }))} className={inputCls} /></div>
                  <div className="sm:col-span-2"><div className={labelCls}>Ort</div><Input value={editForm.ort} onChange={e => setEditForm(p => ({
                  ...p,
                  ort: e.target.value
                }))} className={inputCls} /></div>
                </div>
                <div><div className={labelCls}>Kontakt vor Ort</div><Input value={editForm.kontakt_vor_ort_name} onChange={e => setEditForm(p => ({
                ...p,
                kontakt_vor_ort_name: e.target.value
              }))} className={inputCls} placeholder="Name" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><div className={labelCls}>Telefon</div><Input value={editForm.kontakt_vor_ort_tel} onChange={e => setEditForm(p => ({
                  ...p,
                  kontakt_vor_ort_tel: e.target.value
                }))} className={inputCls} /></div>
                  <div><div className={labelCls}>E-Mail</div><Input value={editForm.kontakt_vor_ort_email} onChange={e => setEditForm(p => ({
                  ...p,
                  kontakt_vor_ort_email: e.target.value
                }))} className={inputCls} /></div>
                </div>
                <div><div className={labelCls}>Zugangsinfo</div><Textarea value={editForm.zugang_info} onChange={e => setEditForm(p => ({
                ...p,
                zugang_info: e.target.value
              }))} className={`${inputCls} h-20`} /></div>
                <div><div className={labelCls}>Notizen</div><Textarea value={editForm.notizen} onChange={e => setEditForm(p => ({
                ...p,
                notizen: e.target.value
              }))} className={`${inputCls} h-20`} /></div>
                <div className="flex justify-end gap-2 pt-1">
                  <AppButton type="button" onClick={() => setEditing(false)} variant="secondary" size="sm">
                    Abbrechen
                  </AppButton>
                  <AppButton
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    variant="success"
                    size="sm"
                    className={saving ? successButtonLoadingClassName : "disabled:opacity-50"}
                  >
                    {saving ? "Speichern…" : "Speichern"}
                  </AppButton>
                </div>
              </div> : <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-blue-100/80">
                  <Field label="Standort" value={adresseStr || "–"} />
                  <Field
                    label="Kunde"
                    value={
                      <AppButton
                        type="button"
                        variant="ghost"
                        onClick={() => navigate(`/kunden/${objekt.kunde_id}`)}
                        className="!p-0 h-auto min-h-0 !border-0 !shadow-none hover:underline font-normal text-blue-300 text-left"
                      >
                        {kundenLabel || `Kunde #${objekt.kunde_id}`}
                      </AppButton>
                    }
                  />
                  {objekt.kontakt_vor_ort_name ? (
                    <Field
                      label="Kontakt vor Ort"
                      value={
                        <span className="inline-flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 opacity-50 shrink-0" aria-hidden />
                          {objekt.kontakt_vor_ort_name}
                        </span>
                      }
                    />
                  ) : null}
                  {objekt.kontakt_vor_ort_tel ? (
                    <Field
                      label="Telefon"
                      value={
                        <a href={`tel:${objekt.kontakt_vor_ort_tel}`} className="underline underline-offset-2 hover:text-blue-200 tabular-nums">
                          {objekt.kontakt_vor_ort_tel}
                        </a>
                      }
                    />
                  ) : null}
                  {objekt.kontakt_vor_ort_email ? (
                    <Field
                      label="E-Mail"
                      value={
                        <a href={`mailto:${objekt.kontakt_vor_ort_email}`} className="underline underline-offset-2 hover:text-blue-200 break-all">
                          {objekt.kontakt_vor_ort_email}
                        </a>
                      }
                    />
                  ) : null}
                  {objekt.zugang_info ? (
                    <div className="min-w-0 sm:col-span-2">
                      <Field
                        label="Zugangsinfo"
                        value={<span className="whitespace-pre-line">{objekt.zugang_info}</span>}
                      />
                    </div>
                  ) : null}
                  {objekt.notizen ? (
                    <div className="min-w-0 sm:col-span-2">
                      <Field
                        label="Notizen"
                        value={<span className="whitespace-pre-line">{objekt.notizen}</span>}
                      />
                    </div>
                  ) : null}
                  {objekt?.id ? (
                    <div className="min-w-0 sm:col-span-2">
                      <EntityInlineFileLinks
                        {...(buildObjektInlineFiles(objekt.id) || {})}
                        className="border-t-0 pt-0"
                        documentsSectionLabel="Objektdokumente"
                      />
                    </div>
                  ) : null}
                </div>
                {adresseStr ? <DetailMapsRouteLink address={adresseStr} /> : null}
              </>}
          </section>

          {/* ──── Zusammenfassung (kompakte Inline-Leiste) ──── */}
          {(() => {
            const stripItems = buildObjektCountsStripItems(objekt);
            if (!stripItems.length) return null;
            return <EntityCountsStrip items={stripItems} />;
          })()}

          {objekt?.kunde_id ? <KundenBlockOfferten kundenId={objekt.kunde_id} objektId={Number(id)} scope="objekt" /> : null}

          <CollapsibleBlock
            compact
            title="📁 Projekte"
            headerInfo={formatAktivArchiviertPair(projekte.length, archivedProjekte.length)}
            panelId="objekt-projekte-panel"
            defaultOpen={false}
            contentClassName="px-3 sm:px-4 pb-3 sm:pb-4"
          >
            <ObjektProjekteList
              projekte={projekte}
              archivedProjekte={archivedProjekte}
              archivPersistKey={`objekt-${id}-proj-arch`}
            />
          </CollapsibleBlock>

          <KundenBlockServiceauftraege objektId={Number(id)} compact />

          {showZugaenge && (
            <CollapsibleBlock
              compact
              title="🔐 Zugänge"
              panelId="objekt-zugaenge-panel"
              defaultOpen={false}
              contentClassName="px-3 sm:px-4 pb-3 sm:pb-4"
            >
              <div className="space-y-3">
                {showS && <WartungUndZugaenge kundeId={objekt.kunde_id} projektId={null} modul="sicherheit" />}
                {showL && <WartungUndZugaenge kundeId={objekt.kunde_id} projektId={null} modul="ladestation" />}
                {showP && <WartungUndZugaenge kundeId={objekt.kunde_id} projektId={null} modul="pv" />}
              </div>
            </CollapsibleBlock>
          )}

          {pendenzenCount > 0 && <ObjektBlockPendenzen objektId={Number(id)} onEmpty={() => setPendenzenCount(0)} />}

          <ObjektBlockDateien objektId={Number(id)} />
          <div className="px-1 space-y-2">
            <SharePointEntityLink entityType="objekt" entityId={Number(id)} />
            <DmsTreePanel entityKind="objekt" entityId={Number(id)} />
          </div>

          <CollapsibleBlock
            compact
            title="📋 Messprotokolle"
            panelId="objekt-messprotokolle-panel"
            defaultOpen={false}
            contentClassName="px-3 sm:px-4 pb-3 sm:pb-4"
          >
            <ObjektMessprotokolleList objektId={Number(id)} />
          </CollapsibleBlock>

          <ObjektBlockKommentare objektId={Number(id)} />

          <ModalOrSheet open={transferOpen} onClose={() => {
          if (!transferSubmitting) closeTransferModal();
        }} title="Verwaltung wechseln" ariaLabel="Objekt einer anderen Verwaltung zuordnen" maxWidth="lg" footer={<div className="flex flex-wrap gap-2 justify-end w-full">
                <AppButton type="button" variant="secondary" size="sm" disabled={transferSubmitting} onClick={closeTransferModal}>
                  Abbrechen
                </AppButton>
                <AppButton type="button" variant="primary" size="sm" disabled={transferSubmitting || transferPreviewLoading || !transferPreview?.ok || transferReason.trim().length < 3} onClick={handleTransferSubmit} className={transferSubmitting ? "opacity-70" : ""}>
                  {transferSubmitting ? "Wird verschoben…" : "Jetzt zuordnen"}
                </AppButton>
              </div>}>
            <div className={`space-y-4 text-sm ${"text-blue-100/90"}`}>
              <p className={subtleCls}>
                Das Objekt samt verknüpfter Auftragshistorie (Projekte, Service, Wartungsverträge, Pendenzen, Kalenderbezug) wird dem gewählten Kunden-Stamm zugeordnet.
                Freitextfelder wie «Verwaltung» auf alten Rapporten werden nicht automatisch geändert.
              </p>
              <div>
                <div className={`${labelCls} !mb-1`}>Aktueller Kunde</div>
                <div className={valCls}>{kundenLabel || `Kunde #${objekt.kunde_id}`}</div>
              </div>
              <div className="relative">
                <div className={`${labelCls} !mb-1`}>Ziel-Verwaltung (Kunde)</div>
                <AppButton type="button" variant="secondary" size="sm" onClick={() => setTransferKundeOpen(v => !v)} className={`w-full !justify-start text-left !h-auto min-h-[44px] font-normal ${"bg-[#0b1324] border-white/10 text-blue-100"}`}>
                  {transferPreview?.to_kunde_label || transferKundeOpts.find(o => Number(o.id) === Number(transferTargetId))?.name || (transferTargetId ? `Kunde #${transferTargetId}` : "Kunde wählen…")}
                </AppButton>
                {transferKundeOpen && <div className={`absolute z-[9000] mt-1 w-full max-w-md rounded-xl border shadow-xl ${"border-white/10 bg-[#0b1324]"}`}>
                    <div className="p-2">
                      <Input value={transferKundeQ} onChange={e => {
                    const v = e.target.value;
                    setTransferKundeQ(v);
                    clearTimeout(transferKundeDebounceRef.current);
                    transferKundeDebounceRef.current = setTimeout(() => loadTransferKunden(v.trim()), 200);
                  }} placeholder="Kunde suchen…" className={inputCls} autoFocus />
                    </div>
                    <div className="max-h-52 overflow-y-auto px-1 pb-2">
                      {transferKundeLoading && <div className={`px-3 py-2 text-xs ${subtleCls}`}>Lade…</div>}
                      {!transferKundeLoading && transferKundeOpts.length === 0 && <div className={`px-3 py-2 text-xs ${subtleCls}`}>Keine Treffer.</div>}
                      {transferKundeOpts.map(o => {
                    const oid = Number(o.id);
                    if (oid === Number(objekt.kunde_id)) return null;
                    return <AppButton key={o.id} type="button" variant="ghost" onClick={() => {
                      setTransferTargetId(oid);
                      setTransferKundeOpen(false);
                    }} className={`w-full !justify-start text-left !h-auto min-h-0 py-2 px-3 rounded-lg font-normal ${oid === Number(transferTargetId) ? "bg-white/10" : "hover:bg-white/5 text-blue-100"}`}>
                            {o.name || `Kunde #${o.id}`}
                          </AppButton>;
                  })}
                    </div>
                  </div>}
              </div>
              <div className={`rounded-xl border px-3 py-2 ${"border-white/10 bg-white/5"}`}>
                <div className={`text-xs font-medium uppercase tracking-wide mb-2 ${subtleCls}`}>Vorschau Änderungen</div>
                {transferPreviewLoading && <div className={subtleCls}>Berechne…</div>}
                {!transferPreviewLoading && transferTargetId && Number(transferTargetId) === Number(objekt.kunde_id) && <div className={subtleCls}>Bitte einen anderen Kunden wählen.</div>}
                {!transferPreviewLoading && transferTargetId && Number(transferTargetId) !== Number(objekt.kunde_id) && transferPreview?.impact && <ul className={`space-y-1 text-[13px] ${valCls}`}>
                    <li>Projekte (Kunden-ID): {transferPreview.impact.projekte_kunde_updates}</li>
                    <li>Projekte (Verwaltungs-ID): {transferPreview.impact.projekte_verwaltung_updates}</li>
                    <li>Serviceaufträge: {transferPreview.impact.serviceauftraege}</li>
                    <li>Wartungsverträge: {transferPreview.impact.wartungsvertraege}</li>
                    <li>Pendenzen: {transferPreview.impact.pendenzen}</li>
                    <li>Mängelberichte (über Service): {transferPreview.impact.mangelberichte}</li>
                    <li>Kalendereinträge (Service): {transferPreview.impact.kalender_service}</li>
                    <li>Kalendereinträge (Projekt): {transferPreview.impact.kalender_projekt}</li>
                  </ul>}
                {!transferPreviewLoading && transferTargetId && Number(transferTargetId) !== Number(objekt.kunde_id) && !transferPreview?.impact && <div className={subtleCls}>Vorschau konnte nicht geladen werden.</div>}
                {!transferTargetId && <div className={subtleCls}>Zuerst Ziel-Kunde wählen.</div>}
              </div>
              <div>
                <div className={`${labelCls} !mb-1`}>Grund (Audit-Log, mind. 3 Zeichen)</div>
                <Textarea value={transferReason} onChange={e => setTransferReason(e.target.value)} className={`${inputCls} min-h-[88px]`} placeholder="z. B. Liegenschaftswechsel von Rieder Widmer zu Livit per 1.4.2026" disabled={transferSubmitting} />
              </div>
            </div>
          </ModalOrSheet>

        </div>
      </div>
    </div>;
}